/**
 * Keeps the browser from scaling the page.
 *
 * `touch-action` in base.css and `user-scalable=no` in the viewport meta cover
 * most browsers, but two ignore both:
 *
 *   - **Safari on iOS** has not honoured `user-scalable=no` since iOS 10, and
 *     runs its own `gesturestart` / `gesturechange` pinch on top of the page.
 *   - **every desktop browser** treats ctrl (or ⌘) plus the wheel as "zoom the
 *     page", which is what a trackpad pinch sends.
 *
 * Both are refused here. Nothing else is: a plain wheel still scrolls, and the
 * keyboard's own zoom (⌘+, ⌘-) is deliberately left alone — that is a person
 * deciding, not a gesture misfiring.
 *
 * Anything that zooms *itself* — the tree, the globe, a constellation, the
 * Moments grid — handles the same gestures on its own element and is
 * unaffected, because this only prevents the browser's default.
 */
export function blockPageZoom(): void {
  const stop = (e: Event) => e.preventDefault();
  // Safari's own pinch, which no CSS can reach.
  for (const name of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(name, stop, { passive: false });
  }
  // A trackpad pinch, which arrives as ctrl + wheel. A wheel on its own scrolls.
  document.addEventListener(
    'wheel',
    (e) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    },
    { passive: false },
  );
}
